<script lang="ts">
  import { fmtCoords } from '../../format'
  import { todayHighLow } from '../../forecast/days'
  import { heroFontSize, heroTemp, NOW_BLOCK, PHONE_NOW } from '../../forecast/hero'
  import { isDryHour, nextHourBars, nextHourSentence } from '../../forecast/nexthour'
  import { hereNote } from '../../state/here.svelte'
  import { location, selectedBundle } from '../../state/location.svelte'
  import { shownAside, shownName } from '../../state/shown'
  import HereMark from '../HereMark.svelte'
  import { withRadar } from '../../forecast/now'
  import { conditionTone } from '../../forecast/tone'
  import { radar, radarHere, radarOverrideFor } from '../../state/now.svelte'

  /**
   * The top of the Forecast section, in Phase 11's drawing (plan §4.1): the
   * redesign's `.head` — a square NOW block in the condition tone beside the
   * place, the sky, the next hour's news and today's high and low. The tone
   * is on the block only; everything else is ink on the ground. The panel bar
   * above it (the mark, the section's name, the star) is `SectionPanel`'s,
   * and the search floats over the map on the desktop (R.4) and sits in the
   * bar on a phone. The details strip and the station line are the section's,
   * under this.
   *
   * It is a *header*: it names a point, so everything it says about the
   * weather comes from `selectedBundle()`, the bundle that is this point's own.
   * While a new point loads, or after its load failed, it names the point and
   * states nothing about it. The sections below keep the previous bundle, on
   * the rule that a stale forecast with an error line beats an empty panel.
   *
   * `phone` is the size only: a 128 px block and a 27 px name.
   */

  let { phone = false }: { phone?: boolean } = $props()

  let point = $derived(location.selected)
  let isHere = $derived(point?.place === 'here')
  // A nameless point that is the phone's own position is still "My location"
  // rather than a pair of coordinates in 24 px type.
  let name = $derived(shownName() ?? (isHere ? 'My location' : null))
  let aside = $derived(shownAside() ?? (point?.zip ? point.zip : null))

  let own = $derived(selectedBundle())
  let current = $derived(own?.current ?? null)
  // The radar's words replace the station's when it is precipitating here
  // and the station 9 km away has not seen it yet (nowcast plan, Stage A).
  // The temperature and the details stay the station's.
  let shown = $derived(withRadar(current, radarOverrideFor(own)))
  let temp = $derived(current ? heroTemp(current.temperature_f) : '')
  let highLow = $derived(own ? todayHighLow(own.forecast, own.location.timezone) : null)
  // Calm until this point's own bundle says otherwise: a block never takes
  // its colour from the place looked at before it.
  let tone = $derived(conditionTone(own && { current: shown, alerts: own.alerts }))
  // The redesign's `.soon` line: the next hour's sentence, only when it has
  // news. "No precipitation in the next hour" is the usual case and says
  // nothing a dry sky does not.
  let soon = $derived.by(() => {
    const bars = own ? nextHourBars(radarHere(), radar.clock) : null
    return bars && !isDryHour(bars) ? nextHourSentence(bars, radar.clock, true) : ''
  })
</script>

<header class="hero" class:phone>
  {#if point || hereNote()?.kind === 'locating'}
    <div class="head">
      <!-- No number, no colour: an empty block would read as a reading of
           nothing. -->
      <div class="now" class:blank={!temp} data-tone={temp ? tone : null}>
        {#if temp}
          <span class="caps">Now</span>
          <span class="temp" style:font-size={heroFontSize(temp, phone ? PHONE_NOW : NOW_BLOCK)}>{temp}</span>
        {/if}
      </div>
      <div class="place">
        <p class="name">
          {#if isHere}<HereMark />{/if}{point
            ? (name ?? fmtCoords(point.lat, point.lon))
            : 'My location'}
        </p>
        {#if point && name}
          <p class="sub coords">
            {aside ? `${aside} · ` : ''}{fmtCoords(point.lat, point.lon)}
          </p>
        {:else if aside}
          <p class="sub">{aside}</p>
        {/if}
        {#if own && !current}
          <p class="no-obs">No recent observation available from nearby stations.</p>
        {:else if current}
          <p class="desc">{shown?.description ?? ''}</p>
        {/if}
        {#if current && soon}<p class="soon">{soon}</p>{/if}
        {#if current && highLow && (highLow.high !== null || highLow.low !== null)}
          <p class="hl">
            {#if highLow.high !== null}<span>H {heroTemp(highLow.high)}</span>{/if}
            {#if highLow.low !== null}<span>L {heroTemp(highLow.low)}</span>{/if}
          </p>
        {/if}
      </div>
    </div>
  {:else}
    <p class="empty">Click the map or search for a place.</p>
  {/if}
</header>

<style>
  .hero {
    display: flex;
    flex-direction: column;
    padding: 0 var(--gutter) 0;
  }

  p {
    margin: 0;
  }

  .head {
    display: flex;
    align-items: stretch;
    gap: 18px;
    padding: 4px 0 0;
  }

  /* The redesign's NOW square: 140 px, or a third of a narrow sidebar.
     Sized against itself through container units: see heroFontSize. */
  .now {
    container-type: inline-size;
    flex: none;
    width: clamp(112px, 36%, 140px);
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--color-field);
    color: var(--color-on-field);
  }

  /* The redesign's phone poster: a 128 px square, a 27 px name. */
  .phone .head {
    gap: 16px;
    padding-top: 4px;
  }

  .phone .now {
    width: 128px;
    padding: 12px 14px;
  }

  .phone .name {
    font-size: 27px;
  }

  .now.blank {
    background: none;
    border: 2px solid var(--color-divider);
  }

  .caps {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.13em;
    text-transform: uppercase;
  }

  .temp {
    font-weight: 800;
    line-height: 0.9;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .place {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .name {
    margin-top: 2px;
    font-size: 26px;
    font-weight: 800;
    line-height: 1.05;
    overflow-wrap: anywhere;
  }

  .sub {
    font-size: 12px;
    color: var(--color-muted);
  }

  .desc {
    margin-top: 2px;
    font-size: 18px;
    line-height: 1.15;
  }

  .soon {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
  }

  .no-obs {
    font-size: 14px;
    font-weight: 600;
  }

  .hl {
    display: flex;
    gap: 10px;
    font-size: 15px;
    font-weight: 600;
    color: var(--color-muted);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .empty {
    padding: 22px 0;
    font-weight: 600;
  }
</style>
